import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ACCOUNT_BUCKET_DEFS,
  ACCOUNT_KIND_DEFS,
  ACCOUNT_KIND_MAP,
  type AccountBucket,
  type AccountKind,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { AccountEditor } from './AccountEditor';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, plural, WORDS } from '../../lib/format';
import { Icon } from '../../components/Icon';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from '../../components/ui';
import { Avatar, OwnerChip } from '../../components/People';
import { InsightCards, type InsightItem } from '../../components/Insights';
import {
  CardPickerButton,
  applyChoice,
  useCardChoice,
  type CardChoice,
} from '../../components/CardPicker';
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from '../../components/ColumnPicker';
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import { activeFields, useCustomFields } from '../../components/CustomFields';
import { ReachIcons } from '../../components/Reach';
import { AccountQuickActions } from './AccountQuickActions';

/** أيقونة كل دلو — تخصّ هذه الواجهة فلا موضع لها في الكتالوج المشترك. */
const BUCKET_ICONS: Record<AccountBucket, 'check' | 'plus' | 'alert' | 'user'> = {
  active: 'check',
  recent: 'plus',
  stale: 'alert',
  unassigned: 'user',
};

/** ما يُعرض قبل أن يختار المستخدم — الدلاء وحدها، والأنواع تفصيلٌ يطلبه من يريده. */
const DEFAULT_CARDS = ACCOUNT_BUCKET_DEFS.map((d) => d.key as string);

const cardChoices: CardChoice[] = [
  ...ACCOUNT_BUCKET_DEFS.map((d) => ({ key: d.key, label: d.label, group: 'حسب المتابعة' })),
  ...ACCOUNT_KIND_DEFS.map((k) => ({ key: k.key, label: k.label, group: 'حسب النوع' })),
];

interface AccountRow {
  id: string;
  kind: AccountKind;
  name: string;
  legalName: string | null;
  taxNumber: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  industry: string | null;
  companySize: string | null;
  city: string | null;
  address: string | null;
  notes: string | null;
  source: string | null;
  ownerId: string | null;
  isActive: boolean;
  createdAt: string;
  /** الصورة `data:` — تحلّ محلّ حروف الاسم حين توجد. */
  avatarUrl: string | null;
  customFields: Record<string, unknown> | null;
}

interface AccountPage {
  items: AccountRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/**
 * كل ما في نموذج العميل يصلح عمودًا — كقائمة المحتملين وللسبب نفسه.
 *
 * والحقول المخصَّصة تُلحق في وقت التشغيل. و`fixed` على الاسم وحده: هو ما
 * يُعرف به الصفّ.
 */
const BASE_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'الاسم', fixed: true },
  { key: 'kind', label: 'النوع' },
  { key: 'ownerId', label: 'المسؤول' },
  { key: 'reach', label: 'وسيلة التواصل' },
  { key: 'industry', label: 'النشاط' },
  { key: 'city', label: 'المدينة' },
  { key: 'createdAt', label: 'أُضيف في' },
  { key: 'legalName', label: 'الاسم القانوني' },
  { key: 'taxNumber', label: 'الرقم الضريبي' },
  { key: 'phone', label: 'الهاتف' },
  { key: 'email', label: 'البريد' },
  { key: 'website', label: 'الموقع' },
  { key: 'companySize', label: 'حجم الشركة' },
  { key: 'address', label: 'العنوان' },
  { key: 'source', label: 'المصدر' },
  { key: 'notes', label: 'ملاحظات' },
];

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في accounts.service.ts. */
const ACCOUNT_FILTERS: FilterDef[] = [
  { key: 'name', label: 'الاسم', kind: 'text', sortable: true },
  {
    key: 'kind',
    label: 'النوع',
    kind: 'enum',
    options: ACCOUNT_KIND_DEFS.map((k) => ({ value: k.key, label: k.label })),
  },
  { key: 'ownerId', label: 'المسؤول', kind: 'user', allowNone: true },
  { key: 'industry', label: 'النشاط', kind: 'text' },
  { key: 'city', label: 'المدينة', kind: 'text' },
  { key: 'createdAt', label: 'أُضيف في', kind: 'date', sortable: true },
  { key: 'legalName', label: 'الاسم القانوني', kind: 'text' },
  { key: 'taxNumber', label: 'الرقم الضريبي', kind: 'text' },
  { key: 'phone', label: 'الهاتف', kind: 'text' },
  { key: 'email', label: 'البريد', kind: 'text' },
  { key: 'website', label: 'الموقع', kind: 'text' },
  { key: 'companySize', label: 'حجم الشركة', kind: 'text' },
  { key: 'address', label: 'العنوان', kind: 'text' },
  { key: 'source', label: 'المصدر', kind: 'text' },
  { key: 'notes', label: 'ملاحظات', kind: 'text' },
];

/** ما يُخفى قبل أن يختار المستخدم — الجدول كما كان، فلا يتغيّر شيء على من لم يختر. */
const DEFAULT_HIDDEN = [
  'legalName',
  'taxNumber',
  'phone',
  'email',
  'website',
  'companySize',
  'address',
  'source',
  'notes',
];

export function AccountsList() {
  const navigate = useNavigate();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const [creating, setCreating] = useState(false);
  const debounced = useDebounced(search);

  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['accounts', query],
    queryFn: () => api<AccountPage>(`/crm/accounts${qs(query)}`),
  });

  /*
   * `bucket` و`page` مستبعدان من استعلام الأعداد.
   *
   * الأول لأن كل بطاقة تعدّ دلوها وحده — تمريره يجعلها تعدّ نفسها فقط.
   * والثاني لأن الأعداد للمجموعة كلها لا للصفحة المعروضة.
   */
  const { bucket: _bucket, page: _page, ...statsQuery } = query as Record<string, unknown>;

  const stats = useQuery({
    queryKey: ['accounts', 'stats', statsQuery],
    queryFn: () => api<Record<string, number>>(`/crm/accounts/stats${qs(statsQuery)}`),
  });

  const { chosen, setChosen, reset: resetCards } = useCardChoice(
    'goyone.cards.accounts',
    DEFAULT_CARDS,
  );

  const allCards: InsightItem[] = [
    ...ACCOUNT_BUCKET_DEFS.map((d) => ({
      key: d.key,
      label: d.label,
      count: stats.data?.[d.key] ?? 0,
      icon: BUCKET_ICONS[d.key],
      color: d.color,
      hint: d.hint,
    })),
    ...ACCOUNT_KIND_DEFS.map((k) => ({
      key: k.key,
      label: k.label,
      count: stats.data?.[k.key] ?? 0,
      icon: (k.key === 'COMPANY' ? 'building' : 'user') as 'building' | 'user',
      color: '#3672EA',
      hint: `عدد ${k.label} في القائمة.`,
    })),
  ];

  const customFields = useCustomFields('ACCOUNT');
  const columns = useMemo<ColumnDef[]>(
    () => [
      ...BASE_COLUMNS,
      ...activeFields(customFields.data).map((f) => ({
        key: `cf:${f.key}`,
        label: f.label,
        group: 'حقول مخصَّصة',
      })),
    ],
    [customFields.data],
  );

  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice('goyone.columns.accounts', columns, DEFAULT_HIDDEN);
  const colDrag = useColumnDrag(columnState, setColumnState);

  return (
    <div className="page">
      <PageHead
        title="العملاء"
        subtitle={list.data ? plural(list.data.total, WORDS.account) : 'العملاء بعد التأهيل'}
      />

      {creating && <AccountEditor onClose={() => setCreating(false)} />}

      {/*
        «عالي القيمة» غائب عمدًا من البطاقات: تعريفه قرار عمل لا هندسة —
        أهو الأعلى فوترةً أم تحصيلًا؟ وفوق أي حدّ أم أعلى عشرة؟ اختيار أحدها
        بلا سؤال يضع رقمًا يبني عليه المدير قرارًا وهو لا يعرف كيف حُسب.
      */}
      <InsightCards
        items={applyChoice(allCards, chosen)}
        active={state.bucket ?? state.kind}
        onPick={(next) => {
          /* الدلو والنوع مرشّحان مختلفان: اختيار أحدهما يمسح الآخر. */
          const isKind = ACCOUNT_KIND_DEFS.some((k) => k.key === next);
          patch(
            next === undefined
              ? { bucket: undefined, kind: undefined }
              : isKind
                ? { kind: next, bucket: undefined }
                : { bucket: next, kind: undefined },
          );
        }}
        pending={stats.isPending}
        /*
         * لا حصص: الدلاء تتقاطع — العميل الواحد نشط وأُضيف حديثًا وبلا
         * مسؤول معًا. شريط حصّة على مجموع يتجاوز عدد العملاء يكذب.
         */
        showShare={false}
      />

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو الرقم الضريبي أو الهاتف"
        />

        {/* الفصل بين الأفراد والشركات كما يظهر في القائمة الجانبية للأنظمة المشابهة. */}
        <div className="row" style={{ gap: 2 }}>
          <button
            className={`btn btn-sm${!state.kind ? ' btn-primary' : ''}`}
            onClick={() => patch({ kind: undefined })}
          >
            الكل
          </button>
          {ACCOUNT_KIND_DEFS.map((k) => (
            <button
              key={k.key}
              className={`btn btn-sm${state.kind === k.key ? ' btn-primary' : ''}`}
              onClick={() => patch({ kind: k.key })}
            >
              {k.label}
            </button>
          ))}
        </div>

        {/*
          الإضافة والأعمدة في الشريط اللاصق — كقائمة المحتملين وللسبب نفسه:
          الشريط يبقى ظاهرًا مهما نزل المستخدم.
        */}
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={ACCOUNT_FILTERS} filters={filters} onChange={setFilters} />
          <ColumnPickerButton
            columns={columns}
            state={columnState}
            onChange={setColumnState}
            onReset={resetColumns}
            touched={columnsTouched}
          />
          <CardPickerButton
            choices={cardChoices}
            chosen={chosen}
            onChange={setChosen}
            onReset={resetCards}
          />
          <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            <Icon name="plus" size={14} />
            عميل جديد
          </button>
        </span>
      </div>

      <FilterChips defs={ACCOUNT_FILTERS} filters={filters} onChange={setFilters} />

      <FilterScope
        defs={ACCOUNT_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/crm/accounts/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            {/*
              الإجراءات أولًا: الصفحة من اليمين إلى اليسار، فأوّل عمود يُرسم
              في أقصى اليمين تحت الإبهام ومسار العين.
            */}
            <thead>
              <tr>
                <th className="col-act" />
                {shownColumns.map((c) => (
                  <th key={c.key} {...colDrag.thProps(c)}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length + 1} />}

            {list.data && (
              <tbody>
                {list.data.items.map((a) => (
                  <tr
                    key={a.id}
                    className="clickable"
                    onClick={() => navigate(`/crm/accounts/${a.id}`)}
                  >
                    <td className="col-act" onClick={(e) => e.stopPropagation()}>
                      <AccountQuickActions
                        account={a}
                        onChanged={() => void list.refetch()}
                      />
                    </td>
                    {shownColumns.map((c) => (
                      <Cell key={c.key} column={c} account={a} />
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا يوجد عملاء"
            text="يُنشأ العميل بتحويل عميل محتمل، أو يُضاف مباشرة."
            action={
              <button className="btn" onClick={() => navigate('/crm/leads')}>
                <Icon name="funnel" size={15} />
                الذهاب إلى العملاء المحتملين
              </button>
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>
    </div>
  );
}

/* ==========================================================================
   الخلية
   ========================================================================== */

/** خليّةٌ تعرف نفسها بمفتاحها لا بموضعها — الأعمدة صارت تُرتَّب وتُخفى. */
function Cell({ column, account }: { column: ColumnDef; account: AccountRow }) {
  if (column.key.startsWith('cf:')) {
    const raw = (account.customFields ?? {})[column.key.slice(3)];
    const text =
      raw === null || raw === undefined || raw === ''
        ? '—'
        : raw === true
          ? 'نعم'
          : raw === false
            ? 'لا'
            : String(raw);
    return <td className="cell-mute">{text}</td>;
  }

  switch (column.key) {
    case 'name':
      return (
        <td>
          <div className="row" style={{ gap: 8 }}>
            <Avatar name={account.name} src={account.avatarUrl} size={28} />
            <span className="cell-strong">{account.name}</span>
          </div>
        </td>
      );

    case 'kind':
      return (
        <td>
          <Badge kind={ACCOUNT_KIND_MAP[account.kind]?.kind}>
            {ACCOUNT_KIND_MAP[account.kind]?.label}
          </Badge>
        </td>
      );

    case 'ownerId':
      return (
        <td>
          <OwnerChip ownerId={account.ownerId} size={22} />
        </td>
      );

    case 'reach':
      return (
        <td onClick={(e) => e.stopPropagation()}>
          <ReachIcons
            name={account.name}
            phone={account.phone}
            email={account.email}
            link={{ accountRefId: account.id }}
          />
        </td>
      );

    case 'industry':
      return <td className="cell-mute">{account.industry ?? '—'}</td>;

    case 'city':
      return <td className="cell-mute">{account.city ?? '—'}</td>;

    case 'createdAt':
      return <td className="cell-mute">{date(account.createdAt)}</td>;

    case 'legalName':
      return <td className="cell-mute">{account.legalName ?? '—'}</td>;

    case 'taxNumber':
      return <td className="cell-mute num">{account.taxNumber ?? '—'}</td>;

    case 'phone':
      return <td className="cell-mute num">{account.phone ?? '—'}</td>;

    case 'email':
      return (
        <td className="cell-mute" dir="ltr">
          {account.email ?? '—'}
        </td>
      );

    case 'website':
      return (
        <td className="cell-mute" dir="ltr">
          {account.website ?? '—'}
        </td>
      );

    case 'companySize':
      return <td className="cell-mute">{account.companySize ?? '—'}</td>;

    case 'address':
      return <td className="cell-mute cell-clip">{account.address ?? '—'}</td>;

    case 'source':
      return <td className="cell-mute">{account.source ?? '—'}</td>;

    case 'notes':
      return (
        <td className="cell-mute cell-clip" title={account.notes ?? undefined}>
          {account.notes ?? '—'}
        </td>
      );

    default:
      return <td className="cell-mute">—</td>;
  }
}
